import { STATES } from '../states';
import { EVENTS } from '../events';
import { PROVIDERS } from '../providers';
import type { PlaylistItem } from '../playlist-item';
import { GoMediaPlayer } from '..';
import { GoMediaPlayerError } from '../errors/error';
import { Timer } from '../../timer';
import { States } from '../../video-scene/state-emitter/state-emitter';
// Webpack 4 prevents us from spying on methods imported using
// `import * as moduleName` syntax. This syntax allows us to spy.
// const OT = require('@opentok/client').default;
// Fixing live session review (DEV-8966)
// Will revisit after this P1 releases
import * as OT from '@opentok/client';
import { NgxLoggerService } from '@go-modules/services/logger/logger.service';

export interface OpenTokSubscriberOptions extends OT.SubscriberProperties {
	api_key: string;
	session_id: string;
	token: string;
	containerElement?: HTMLElement;
	hasUsedScreenCapture: boolean;
}

export interface OpenTokSession extends OT.Session {
	connections?: any;
	permissions?: any;
	streams?: any;
	emit: (arg1: string, arg2?: any) => void;
}

export interface OpenTokConnection extends OT.Connection {
	permissions: any;
}

export class OpenTokStreamSubscriberAdapter extends GoMediaPlayer {
	public static WRAP_UP_TIMEOUT = 3000;
	public static RECORDING_STATE_CHANGE = 'recordingStateChange';

	public timer: Timer;
	public volume: number;
	public volumeBeforeMute: number;
	public captionsOn: boolean = false;
	public audioContext: AudioContext = new (window.AudioContext || window.webkitAudioContext)();
	// https://github.com/microsoft/TypeScript/issues/1778 - have to use string instead of alias
	public audioSources: {[key: /*OT.Stream['streamId']*/ string]: MediaStreamAudioSourceNode } = {};
	public audioDestination: MediaStreamAudioDestinationNode = this.audioContext.createMediaStreamDestination();
	public wrapUpTimeout: ReturnType<typeof setTimeout> = null;

	protected alwaysEmitTimeEvent: boolean = true;
	private acceptNextElapsedTimeSync: boolean = false;

	private constructor (public session: OpenTokSession, public options: OpenTokSubscriberOptions) {
		super();
		this.volume = 0;
		this.volumeBeforeMute = this.volume;
		this.timer = new Timer();
	}

	public static setup (options: OpenTokSubscriberOptions): Promise<OpenTokStreamSubscriberAdapter> {
		return new Promise((resolve, reject) => {
			const session = OT.initSession(options.api_key, options.session_id) as OpenTokSession;
			session.connect(options.token, (error) => {
				if (error) {
					reject(new GoMediaPlayerError(error.message));
					return;
				}
				const adapter = new OpenTokStreamSubscriberAdapter(session, options);
				adapter.initEventEmulation();
				adapter.init();
				resolve(adapter);
			});
		});
	}

	public play (): void {
		const promises = [];
		const streams = this.getStreams().filter((stream) => {
			return !this.session.getSubscribersForStream(stream).length;
		});

		streams.forEach((stream) => {
			const promise = this.createSubscriber(stream)
				.catch((error) => {
					this.emit(EVENTS.ERROR, error);
				});
			promises.push(promise);
		});

		Promise.all(promises).then(() => {
			this.setState(STATES.PLAYING);
		});
	}

	public pause (): void {
		throw new GoMediaPlayerError('Method not supported!');
	}

	public seekTo (_time: number): void {
		throw new GoMediaPlayerError('Method not supported!');
	}

	public stop (): void {
		this.getSubscribers().forEach((subscriber) => {
			this.session.unsubscribe(subscriber);
		});

		this.setState(STATES.IDLE);
	}

	public getTime (): number {
		return this.timer.getTime();
	}

	public getDuration (): number {
		return this.getTime();
	}

	public getElement (): HTMLElement {
		return this.options.containerElement;
	}

	public destroy (): void {
		for (const audioSource of Object.values(this.audioSources)) {
			audioSource.disconnect();
		}
		this.audioSources = {};
		this.audioDestination.disconnect();
		this.audioContext?.close();
		this.audioContext = null;
		this.timer.destroy();
		this.session.off('archiveStarted', this.onArchiveStarted);
		this.session.off('archiveStopped', this.onArchiveStopped);
		this.session.off('connectionCreated', this.onConnectionCreated);
		this.session.off('connectionDestroyed', this.onConnectionDestroyed);
		this.session.off('signal:elapsedTime', this.onElapsedTime);
		this.session.off('signal:state', this.onState);
		this.session.off('streamCreated', this.onStreamCreated);
		this.session.off('streamDestroyed', this.onStreamDestroyed);
		this.session.disconnect();
		super.destroy();
	}

	public getMute (): boolean {
		return this.getVolume() === 0;
	}

	public async setMute (value?: boolean): Promise<void> {
		if (value) {
			this.volumeBeforeMute = this.getVolume();
			this.setVolume(0);
		} else {
			this.setVolume(this.volumeBeforeMute);
		}
	}

	public setVolume (value: number): void {
		this.volume = value;
		this.getSubscribers().forEach((subscriber) => {
			subscriber.setAudioVolume(value);
		});
	}

	public getVolume (): number {
		return this.volume;
	}

	public isPlaybackRateSupported (): boolean {
		return false;
	}

	public get playbackRate (): number {
		throw new GoMediaPlayerError('Getter not supported!');
	}

	public set playbackRate (value: number) {
		throw new GoMediaPlayerError('Setter not supported!');
	}

	public captionsEnabled (): boolean {
		return this.captionsOn;
	}

	public toggleCaptions (enable: boolean = !this.captionsOn): void {
		this.captionsOn = enable;

		if (this.captionsOn) {
			this.emit(EVENTS.AUDIO_STREAM, this.audioDestination.stream);
		} else {
			this.emit(EVENTS.AUDIO_STREAM, null);
		}
	}

	public hasCaptions (): boolean {
		return true;
	}

	public getPlaylistItem (): PlaylistItem {
		throw new GoMediaPlayerError('Method not supported!');
	}

	public getProvider (): {name: PROVIDERS} {
		return { name: PROVIDERS.OPENTOK };
	}

	public getNumSubscriberConnections (): number {
		return this.getSubscriberConnections().length;
	}

	public getConnections (): OpenTokConnection[] {
		return this.session.connections.map((connection) => {
			return connection;
		}) as OpenTokConnection[];
	}

	public getSubscriberConnections (): OT.Connection[] {
		return this.getConnections().filter((connection) => {
			return !connection.permissions.publish;
		});
	}

	public getStreams (): OT.Stream[] {
		return this.session.streams.map((stream) => {
			return stream;
		});
	}

	public getStreamCount (): number {
		return this.getStreams().length;
	}

	public getSubscribers (): OT.Subscriber[] {
		const subscribers: OT.Subscriber[] = [];
		this.getStreams().forEach((stream) => {
			this.session.getSubscribersForStream(stream).forEach((subscriber) => {
				subscribers.push(subscriber);
			});
		});
		return subscribers;
	}

	public createSubscriber (stream: OT.Stream): Promise<OT.Subscriber> {
		return new Promise((resolve, reject) => {
			const subscriber = this.session.subscribe(stream, this.options.containerElement, this.options, (error) => {
				if (error) {
					reject(new GoMediaPlayerError(error.message));
					return;
				}
				const mediaStream = subscriber._.webRtcStream();
				if (mediaStream.getAudioTracks().length) {
					const source = this.audioContext.createMediaStreamSource(mediaStream);
					this.audioSources[stream.streamId] = source;
					// For whatever reason, connecting immediately can cause issues
					// with other participants hearing audio. Having a setTimeout
					// seems to resolve the issue
					setTimeout(() => source.connect(this.audioDestination));
				}

				resolve(subscriber);
			});
			subscriber.setAudioVolume(this.volume);
		});
	}

	public init (): void {
		this.timer.start();

		this.session.on('archiveStarted', this.onArchiveStarted);
		this.session.on('archiveStopped', this.onArchiveStopped);
		this.session.on('connectionCreated', this.onConnectionCreated);
		this.session.on('connectionDestroyed', this.onConnectionDestroyed);
		this.session.on('signal:elapsedTime', this.onElapsedTime);
		this.session.on('signal:state', this.onState);
		this.session.on('streamCreated', this.onStreamCreated);
		this.session.on('streamDestroyed', this.onStreamDestroyed);

		this.requestElapsedTime();
	}

	public requestElapsedTime (): void {
		try {
			this.session.signal({
				type: 'elapsedTimeRequest'
			}, (error) => {
				if (error) NgxLoggerService.logError(error);
			});
		} catch (error) {
			NgxLoggerService.logError(error);
		}
	}

	public onConnectionCreated = () => {
		this.emit(EVENTS.SUBSCRIBER_CONNECTION_COUNT, { count: this.getNumSubscriberConnections() });
	};

	public onConnectionDestroyed = () => {
		this.emit(EVENTS.SUBSCRIBER_CONNECTION_COUNT, { count: this.getNumSubscriberConnections() });
	};

	public onArchiveStarted = () => {
		this.syncRecordingState(States.RECORDING);
	};

	public onArchiveStopped = () => {
		this.syncRecordingState(States.PAUSED);
	};

	public onElapsedTime = (event) => {
		const elapsedTime = Number(event.data);
		const currentTime = this.timer.getTime();

		if (!Number.isFinite(elapsedTime)) {
			return;
		}

		if (this.acceptNextElapsedTimeSync || elapsedTime > currentTime) {
			this.timer.setTime(elapsedTime);
			this.acceptNextElapsedTimeSync = false;
		}
	};

	public onState = (event) => {
		switch (event.data) {
			case States.RECORDING:
			case States.PAUSED:
			case States.STOPPED:
				this.syncRecordingState(event.data);
				break;
		}
	};

	public onStreamCreated = (event: OT.EventType<OT.Session, 'streamCreated'>) => {
		clearTimeout(this.wrapUpTimeout);
		if (this.isPlaying()) {
			this.createSubscriber(event.stream);
		}
	};

	public onStreamDestroyed = (event: OT.EventType<OT.Session, 'streamDestroyed'>) => {
		this.audioSources[event.stream.streamId].disconnect();
		delete this.audioSources[event.stream.streamId];

		if (this.getStreamCount() === 0) {
			// Sometimes the stream can be destroy/recreated.
			// We don't want to really wrap up if that happens.
			// Wait 3 seconds for reconnect before wrapping up
			clearTimeout(this.wrapUpTimeout);
			this.wrapUpTimeout = setTimeout(() => {
				this.setState(STATES.IDLE);
				this.emit(EVENTS.COMPLETE);
			}, OpenTokStreamSubscriberAdapter.WRAP_UP_TIMEOUT);
		}
	};

	private syncRecordingState (state: States): void {
		switch (state) {
			case States.RECORDING:
				this.resumeTimerFromAuthoritativeTime();
				break;
			case States.PAUSED:
			case States.STOPPED:
				this.pauseTimerAtAuthoritativeTime();
				break;
		}

		this.emit(OpenTokStreamSubscriberAdapter.RECORDING_STATE_CHANGE, {state});
	}

	private pauseTimerAtAuthoritativeTime (): void {
		this.acceptNextElapsedTimeSync = true;
		this.timer.pause();
		this.requestElapsedTime();
	}

	private resumeTimerFromAuthoritativeTime (): void {
		this.acceptNextElapsedTimeSync = true;
		this.timer.start();
		this.requestElapsedTime();
	}
}
